"use client";

import { ContentLink } from "@components/cms/link/ContentLink";
import { CommonCMS } from "@lib/constants/contentful";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ecco/ui/text";
import { Divider } from "@ecco/ui/divider";
import { Button } from "@ecco/ui/button";
import { Box } from "@ecco/ui/box";
import { Container } from "@ecco/ui/container";
import { Flex } from "@ecco/ui/flex";
import type { FC } from "react";
import type { CheckoutFooterModel } from "./mapCheckoutFooter";
import { styles } from "./styles";

export interface CheckOutFooterProps {
    footer: CheckoutFooterModel;
}

export const CheckOutFooter: FC<CheckOutFooterProps> = ({ footer }) => {
    const { get: getMicrocopy } = useMicrocopy();
    const { copyright, columns } = footer;
    const renewCookieLabel = getMicrocopy(CommonCMS.shared, CommonCMS.cookieRenew);

    const hasPolicyLinks = columns.some((column) => column.links.length > 0);

    if (!hasPolicyLinks) return null;

    return (
        <Container sx={styles.mainContainer}>
            <Container sx={styles.container}>
                <Flex data-testid="menu-columns" sx={styles.columns}>
                    {columns.map((column) => (
                        <Box key={column.id} sx={{ ...styles.group, w: column.width }}>
                            <Text
                                sx={styles.groupTitle}
                                data-testid="footer-group-title"
                                textTransform="uppercase"
                            >
                                {getMicrocopy(CommonCMS.shared, column.titleKey) || column.title}
                            </Text>
                            <Flex sx={styles.linkList} data-testid="footer-policies">
                                {column.showCookieConsent && (
                                    <Button
                                        variant="textLink"
                                        data-testid="renewcookie"
                                        aria-label={renewCookieLabel}
                                        sx={styles.button}
                                        onClick={() => window.showCookieBanner()}
                                    >
                                        <span>{renewCookieLabel}</span>
                                    </Button>
                                )}
                                {column.links.map((link) => (
                                    <ContentLink
                                        key={link.id}
                                        link={link.contentLink}
                                        data-testid="item-title"
                                        sx={styles.link}
                                    />
                                ))}
                            </Flex>
                        </Box>
                    ))}
                </Flex>

                <Divider sx={styles.divider} data-testid="divider" />

                <Box sx={styles.copyright} data-testid="footer-copyright-text">
                    {copyright}
                </Box>
            </Container>
        </Container>
    );
};

export default CheckOutFooter;
